import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { find, map } from "lodash";
import { IoChevronDown } from "react-icons/io5";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import CustomerReviews from "@/Component/Common/CustomerReviews";
import FaqSection from "@/Component/Common/FaqSection";
import PageHero from "@/Component/Common/PageHero";
import SeeTheWorld from "@/Component/Common/SeeTheWorld";
import DownloadNH from "@/Component/Common/DownloadNH";
import { fetchDealsModuleData } from "@/services/api/common";
import { isIndianUser } from "@/utils/getCountryCode";
import {
  DEALS_CATEGORIES,
  DEFAULT_DEALS_FAQS,
  hasServiceContent,
  mapApiFaqs,
  slugifySectionId,
} from "@/utils/dealsHelpers";
import CountrywiseCard from "./CountrywiseCard";
import { scrollToSectionById } from "@/utils/scrollToSection";
import styles from "./Deals.module.css";

function getAvailableCategories(services = [], isIndia) {
  const available = DEALS_CATEGORIES.filter((category) =>
    find(services, (service) => service.moduleName === category.dataKey),
  );

  if (isIndia) {
    return available.filter((category) => category.dataKey === "Holiday");
  }

  return available;
}

const REGION_OPTIONS = [
  { value: "UAE", label: "Middle East" },
  { value: "IN", label: "India" },
];

export default function Deals({
  initialDeals = null,
  initialModuleData = [],
  initialReviewsList = null,
}) {
  const contentRef = useRef(null);
  const router = useRouter();
  const [isIndia, setIsIndia] = useState(false);
  const [region, setRegion] = useState("UAE");
  const [activeModule, setActiveModule] = useState("Holiday");
  const [moduleData, setModuleData] = useState(initialModuleData || []);
  const [loadingModule, setLoadingModule] = useState(false);
  const [selectedSection, setSelectedSection] = useState("");

  useEffect(() => {
    const india = isIndianUser();
    setIsIndia(india);
    setRegion(india ? "IN" : "UAE");
  }, []);

  const dealsData = initialDeals || {};
  const services = dealsData.services || [];
  const banner = dealsData.topBanners?.[0];
  const displayCategories = useMemo(
    () => getAvailableCategories(services, isIndia),
    [services, isIndia],
  );

  useEffect(() => {
    if (!displayCategories.length) return;
    if (!find(displayCategories, (item) => item.dataKey === activeModule)) {
      setActiveModule(displayCategories[0].dataKey);
    }
  }, [displayCategories, activeModule]);

  const loadModuleData = useCallback(async (moduleName) => {
    setLoadingModule(true);
    try {
      const response = await fetchDealsModuleData({ ModuleName: moduleName });
      setModuleData(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load deals module data:", error);
      setModuleData([]);
    } finally {
      setLoadingModule(false);
    }
  }, []);

  useEffect(() => {
    if (activeModule === "Holiday" && initialModuleData?.length) {
      setModuleData(initialModuleData);
      return;
    }
    loadModuleData(activeModule);
  }, [activeModule, initialModuleData, loadModuleData]);

  const visibleSections = useMemo(
    () => moduleData.filter(hasServiceContent),
    [moduleData],
  );

  const scrollToSection = (sectionId) => {
    if (typeof window === "undefined") return;

    window.history.pushState(null, "", `#${sectionId}`);
    if (!scrollToSectionById(sectionId)) return;
    setSelectedSection(sectionId);
  };

  useEffect(() => {
    if (!visibleSections.length) {
      setSelectedSection("");
      return;
    }

    const firstId = slugifySectionId(visibleSections[0].btnName);
    setSelectedSection((prev) => {
      const isValid = visibleSections.some(
        (section) => slugifySectionId(section.btnName) === prev,
      );
      return isValid ? prev : firstId;
    });
  }, [visibleSections]);

  useEffect(() => {
    if (!visibleSections.length || typeof window === "undefined") return;

    const hash = window.location.hash.replace("#", "");
    if (!hash) return;

    setTimeout(() => {
      scrollToSection(hash);
    }, 150);
  }, [visibleSections]);

  const faqItems = useMemo(() => {
    const apiFaqs = mapApiFaqs(dealsData.faqs);
    return apiFaqs.length ? apiFaqs : DEFAULT_DEALS_FAQS;
  }, [dealsData.faqs]);

  const seeTheWorldTitle =
    dealsData.heading?.title || "See The World With Next Holidays";
  const seeTheWorldText = dealsData.heading?.description;

  const webImageUrl = banner?.webImageUrl || "/images/hero.png";

  return (
    <MainLayout hideHeader>
      <PageHero
        webImageUrl={webImageUrl}
        alt={banner?.webAltTag || "Deals banner"}
        scrollTargetRef={contentRef}
      />

      {/* <div className={styles.breadcrumbWrap}> */}
        <Breadcrumbs />
      {/* </div> */}

      <div ref={contentRef} className={`standard-padding ${styles.stickyWrap}`}>
        <div className={styles.categoryBar}>
          <div className={styles.categoryTabs}>
            {displayCategories.map((category) => (
              <button
                key={category.dataKey}
                type="button"
                className={`${styles.categoryTab} ${
                  activeModule === category.dataKey
                    ? styles.categoryTabActive
                    : ""
                }`}
                onClick={() => {
                  setActiveModule(category.dataKey);
                  setSelectedSection("");
                }}
              >
                <Image
                  src={category.img}
                  alt=""
                  width={28}
                  height={28}
                  className={`${styles.categoryIcon} ${
                    activeModule === category.dataKey
                      ? styles.categoryIconActive
                      : ""
                  }`}
                  quality={70}
                  loading="lazy"
                />
                {category.text}
              </button>
            ))}
          </div>

          <div className={styles.regionSelectWrap}>
            <select
              className={styles.sectionSelect}
              value={
                selectedSection || slugifySectionId(visibleSections[0].btnName)
              }
              onChange={(event) => scrollToSection(event.target.value)}
              aria-label="Select deal section"
            >
              {visibleSections.map((section) => {
                const sectionId = slugifySectionId(section.btnName);
                return (
                  <option key={sectionId} value={sectionId}>
                    {section.btnName}
                  </option>
                );
              })}
            </select>
            <IoChevronDown
              className={styles.regionSelectChevron}
              aria-hidden="true"
            />
          </div>
          <div
            className={styles.regionTabs}
            role="tablist"
            aria-label="Regions"
          >
            {map(visibleSections, (region) => {
              const isSelected =
                (selectedSection ||
                  slugifySectionId(visibleSections[0].btnName)) ===
                slugifySectionId(region.btnName);
              return (
                <button
                  key={region.btnName}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  className={`${styles.regionTab} ${
                    isSelected ? styles.regionTabActive : ""
                  }`}
                  onClick={() =>
                    scrollToSection(slugifySectionId(region.btnName))
                  }
                >
                  {region.btnName}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {loadingModule ? (
        <div className={styles.loadingWrap}>
          <Image
            src="/assets/loading.gif"
            alt="Loading deals"
            width={100}
            height={100}
            quality={70}
            loading="lazy"
          />
        </div>
      ) : (
        <>
          {/* {visibleSections.length > 0 ? (
            <div className={`standard-padding ${styles.sectionSelectWrap}`}>
              <div className={styles.sectionSelectInner}>
                <select
                  className={styles.sectionSelect}
                  value={
                    selectedSection || slugifySectionId(visibleSections[0].btnName)
                  }
                  onChange={(event) => scrollToSection(event.target.value)}
                  aria-label="Select deal section"
                >
                  {visibleSections.map((section) => {
                    const sectionId = slugifySectionId(section.btnName);
                    return (
                      <option key={sectionId} value={sectionId}>
                        {section.btnName}
                      </option>
                    );
                  })}
                </select>
                <IoChevronDown
                  className={styles.sectionSelectChevron}
                  aria-hidden="true"
                />
              </div>
            </div>
          ) : null} */}

          {map(visibleSections, (section) => {
            const sectionId = slugifySectionId(section.btnName);

            return (
              <div
                key={sectionId}
                id={sectionId}
                className={styles.sectionBlock}
              >
                <CountrywiseCard
                  titleFirst={section.titleFirst}
                  titleLast={section.titleLast}
                  description={section.description}
                  packageList={section.itemsList || []}
                  moduleType={activeModule}
                />
              </div>
            );
          })}

          {!visibleSections.length ? (
            <p className={`nodata ${styles.emptyState}`}>
              No deals available at the moment
            </p>
          ) : null}
        </>
      )}

      <CustomerReviews
        moduleType="Deals"
        initialReviewsList={initialReviewsList}
      />

      <FaqSection
        items={faqItems}
        className="standard-padding"
      />
      <DownloadNH />
      <SeeTheWorld
        title={seeTheWorldTitle}
        text={seeTheWorldText || undefined}
      />
    </MainLayout>
  );
}
